import { useContext } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import Loader from 'components/loader/Loader';
import Button from 'components/button/Button';
import Tooltip from 'components/tooltip/Tooltip';
import { LeadAvailablePipelineAction, LeadSource } from 'models/Leads';
import useLeadActions, { buttonStyles } from '../actions/useLeadActions';
import useLeadActionsPermission from '../actions/useLeadActionsPermission';
import LeadModalContext from './LeadModalContext';
import {
  LeadModalFooterActionsContainer,
  LeadModalFooterAirbnbDocsMessage,
  LeadModalFooterBCDocsMessage,
} from './footer/LeadModalFooterMessages.styles';
import LeadModalFooterMessages from './footer/LeadModalFooterMessages';

const LeadModalFooterActionButtons = ({
  overlappingLeads,
  updateLeadCallback,
  errorMessage,
}: {
  overlappingLeads: string[] | undefined;
  updateLeadCallback: () => void;
  errorMessage: string;
}) => {
  const { lead } = useContext(LeadModalContext);
  const { t } = useTranslation();
  const { airbnbActions, standardActions, isExecutingAction, takeAction } =
    // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    useLeadActions({ lead });
  // @ts-expect-error TS2345 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
  const canExecutePipelineActions = useLeadActionsPermission(lead);

  if (!airbnbActions.length && !standardActions.length) {
    return null;
  }

  if (isExecutingAction) {
    return (
      <LeadModalFooterActionsContainer>
        <Loader />
      </LeadModalFooterActionsContainer>
    );
  }

  const handleAction = (action: LeadAvailablePipelineAction) => {
    takeAction({
      action,
      // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      lead,
      updateLeadCallback,
    });
  };

  // prettier breaks locale Trans component interpolation, so it must be excluded from formatting
  // prettier-ignore
  const airbnbDocsLink = (
    <Trans i18nKey="componentLead.actionsInfo.airbnb.docDetails">
      Please see <a
        href="https://help.hostfully.com/en/articles/4623220-airbnb-inquiry-booking-request-auto-responses"
        rel="noreferrer"
        target="_blank"
      >
        online documentation
      </a> for details
    </Trans>
  );

  const bookingDotComDocsLink = (
    <Trans i18nKey="componentLead.actionsInfo.bookingdotcom.docDetails">
      {`By accepting or declining this request, you acknowledge that you're doing
      so in accordance with Booking.com's`}
      <a
        href="https://partner.booking.com/en-gb/legal-resources/non-discrimination-guidelines-when-accepting-or-declining-booking-request"
        rel="noreferrer"
        target="_blank"
      >
        non-discrimination policy
      </a>
      .
    </Trans>
  );

  const buttonsDisabled = !canExecutePipelineActions;

  const buttons = [...airbnbActions, ...standardActions].map((action) => (
    <Button
      // @ts-expect-error TS7053 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      bsStyle={buttonStyles[action] || 'success'}
      disabled={buttonsDisabled}
      onClick={() => handleAction(action)}
      data-testid={`lead-action-button-${action}`}
      key={`lead-action-button-${action}`}
    >
      {t(`componentLead.actions.${action}`)}
    </Button>
  ));

  return (
    <LeadModalFooterActionsContainer>
      {!!airbnbActions.length && (
        <LeadModalFooterAirbnbDocsMessage>
          <div>{t('componentLead.actionsInfo.airbnb.declineNote')}</div>
          <div>{airbnbDocsLink}</div>
        </LeadModalFooterAirbnbDocsMessage>
      )}

      {lead?.source === LeadSource.DIRECT_BOOKINGDOTCOM && (
        <LeadModalFooterBCDocsMessage data-testid="lead-action-bc-docs-message">
          <div>{bookingDotComDocsLink}</div>
        </LeadModalFooterBCDocsMessage>
      )}

      <LeadModalFooterMessages
        errorMessage={errorMessage}
        overlappingLeads={overlappingLeads}
      />

      {buttonsDisabled
        ? buttons.map((button) => (
            <Tooltip
              id={`${button.key}-tooltip`}
              key={`${button.key}-tooltip`}
              text={t('componentLead.actionsInfo.noPermission')}
            >
              {button}
            </Tooltip>
          ))
        : buttons}
    </LeadModalFooterActionsContainer>
  );
};

export default LeadModalFooterActionButtons;
